import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpClientModule } from '@angular/common/http';
import { firstValueFrom, from } from 'rxjs';

class CookieStore {
    getCookie(key) {
        const cookieCollection = this.getCookieCollection();
        return cookieCollection[key];
    }
    getCookieCollection() {
        const currentRawCookie = document.cookie;
        if (currentRawCookie === this.lastReadRawCookieCollection) {
            return this.lastReadCookieCollection;
        }
        this.lastReadRawCookieCollection = currentRawCookie;
        this.lastReadCookieCollection = {};
        if (this.lastReadRawCookieCollection.indexOf('; ') !== -1) {
            const cookies = this.lastReadRawCookieCollection.split('; ');
            for (const cookie of cookies) {
                const indexOfEqualSign = cookie.indexOf('=');
                if (indexOfEqualSign > 0) {
                    const cookieName = CookieStore.tryDecodeUriComponent(cookie.substring(0, indexOfEqualSign));
                    this.lastReadCookieCollection[cookieName] = CookieStore.tryDecodeUriComponent(cookie.substring(indexOfEqualSign + 1));
                }
            }
        }
        return this.lastReadCookieCollection;
    }
    static tryDecodeUriComponent(uriComponent) {
        try {
            return decodeURIComponent(uriComponent);
        }
        catch (e) {
            return uriComponent;
        }
    }
}

class HttpHeader {
    constructor(key, value) {
        this.key = key;
        this.value = value;
    }
}

class HttpError extends Error {
    constructor(method, url, status, statusText, body) {
        super(`Failed to call api ${method} ${url}, status code: ${status}`);
        this.method = method;
        this.url = url;
        this.status = status;
        this.statusText = statusText;
        this.body = body;
        // Set the prototype explicitly.
        Object.setPrototypeOf(this, new.target.prototype);
    }
}

const DEFAULT_REQUEST_OPTIONS = {};

class CoolHttp {
    constructor(_http) {
        this._http = _http;
        this._cookieStore = new CookieStore();
        this._globalHeaders = [];
        this._requestInterceptors = [];
        this._responseInterceptors = [];
        this._customCookieToHeaders = [];
    }
    get baseUrl() {
        return this._baseUrl;
    }
    registerBaseUrl(baseUrl) {
        this._baseUrl = baseUrl;
        if (this._baseUrl[this._baseUrl.length - 1] !== '/') {
            this._baseUrl += '/';
        }
    }
    deRegisterBaseUrl() {
        this._baseUrl = null;
    }
    setWithCredentials(status) {
        this._withCredentials = status;
    }
    registerGlobalHeader(header) {
        this.deregisterGlobalHeader(header.key);
        this._globalHeaders.push(header);
    }
    getGlobalHeaders() {
        return this._globalHeaders;
    }
    deregisterGlobalHeader(headerKey) {
        const indexOfHeader = this._globalHeaders.findIndex(header => header.key === headerKey);
        if (indexOfHeader === -1) {
            return false;
        }
        this._globalHeaders.splice(indexOfHeader, 1);
        return true;
    }
    removeAllRegisteredGlobalHeaders() {
        this._globalHeaders.length = 0;
    }
    registerRequestInterceptor(requestInterceptor) {
        this._requestInterceptors.push(requestInterceptor);
    }
    deregisterRequestInterceptor(requestInterceptor) {
        const indexOfItem = this._requestInterceptors.indexOf(requestInterceptor);
        if (indexOfItem === -1) {
            return false;
        }
        this._requestInterceptors.splice(indexOfItem, 1);
        return true;
    }
    registerResponseInterceptor(responseInterceptor) {
        this._responseInterceptors.push(responseInterceptor);
    }
    deregisterResponseInterceptor(responseInterceptor) {
        const indexOfItem = this._responseInterceptors.indexOf(responseInterceptor);
        if (indexOfItem === -1) {
            return false;
        }
        this._responseInterceptors.splice(indexOfItem, 1);
        return true;
    }
    sendCookieValueInCustomHeader(cookieName, customHeaderName) {
        this._customCookieToHeaders.push({
            cookieName,
            customHeaderName
        });
    }
    async getAsync(url, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return await that._requestCoreAsync(url, 'GET', null, options, (innerUrl, data, modOptions) => {
            return that._http.get(innerUrl, modOptions);
        });
    }
    async postAsync(url, data, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return await that._requestCoreAsync(url, 'POST', data, options, (innerUrl, innerData, modOptions) => {
            return that._http.post(innerUrl, innerData, modOptions);
        });
    }
    async putAsync(url, data, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return await that._requestCoreAsync(url, 'PUT', data, options, (innerUrl, innerData, modOptions) => {
            return that._http.put(innerUrl, innerData, modOptions);
        });
    }
    async deleteAsync(url, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return await that._requestCoreAsync(url, 'DELETE', null, options, (innerUrl, data, modOptions) => {
            return that._http.delete(innerUrl, modOptions);
        });
    }
    async patchAsync(url, data, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return await that._requestCoreAsync(url, 'PATCH', data, options, (innerUrl, innerData, modOptions) => {
            return that._http.patch(innerUrl, innerData, modOptions);
        });
    }
    async headAsync(url, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return await that._requestCoreAsync(url, 'HEAD', null, options, (innerUrl, data, modOptions) => {
            return that._http.head(innerUrl, modOptions);
        });
    }
    async _requestCoreAsync(url, method, data, options, action) {
        url = this._convertUrl(url);
        const modifiedOptions = this._modifyOptions(options);
        const clientHeaders = this._convertAngularHeadersToHttpClientHeaders(modifiedOptions.headers);
        let shouldIntercept = await this._invokeRequestInterceptorsAsync(url, method, data, clientHeaders);
        if (shouldIntercept) {
            throw new Error('Request intercepted');
        }
        modifiedOptions.headers = this._updateAngularHeadersFromHttpClientHeaders(clientHeaders, modifiedOptions.headers);
        let response;
        try {
            const actionResponse = await firstValueFrom(action(url, data, modifiedOptions));
            if (!actionResponse) {
                throw new Error('No response');
            }
            response = actionResponse;
        }
        catch (errorResponse) {
            response = errorResponse;
        }
        shouldIntercept = await this._invokeResponseInterceptorsAsync(response, url, method, data, clientHeaders);
        if (shouldIntercept) {
            throw new Error('Response intercepted');
        }
        if (!response.ok) {
            throw new HttpError(method, url, response.status, response.statusText, JSON.stringify(response.body));
        }
        let returnValue;
        try {
            returnValue = response.body ? JSON.parse(response.body) : null;
        }
        catch (e) {
            returnValue = { data: response.body };
        }
        return returnValue;
    }
    getObservable(url, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return that._requestCoreObservable(url, 'GET', null, options, (innerUrl, data, modOptions) => {
            return that._http.get(innerUrl, modOptions);
        });
    }
    postObservable(url, data, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return that._requestCoreObservable(url, 'POST', data, options, (innerUrl, innerData, modOptions) => {
            return that._http.post(innerUrl, innerData, modOptions);
        });
    }
    putObservable(url, data, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return that._requestCoreObservable(url, 'PUT', data, options, (innerUrl, innerData, modOptions) => {
            return that._http.put(innerUrl, innerData, modOptions);
        });
    }
    deleteObservable(url, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return that._requestCoreObservable(url, 'DELETE', null, options, (innerUrl, data, modOptions) => {
            return that._http.delete(innerUrl, modOptions);
        });
    }
    patchObservable(url, data, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return that._requestCoreObservable(url, 'PATCH', data, options, (innerUrl, innerData, modOptions) => {
            return that._http.patch(innerUrl, innerData, modOptions);
        });
    }
    headObservable(url, options = DEFAULT_REQUEST_OPTIONS) {
        const that = this;
        return that._requestCoreObservable(url, 'HEAD', null, options, (innerUrl, data, modOptions) => {
            return that._http.head(innerUrl, modOptions);
        });
    }
    _requestCoreObservable(url, method, data, options, action) {
        return from(this._requestCoreAsync(url, method, data, options, action));
    }
    _convertUrl(url) {
        let returnUrl = url;
        if (this._baseUrl) {
            returnUrl = this._baseUrl + returnUrl;
        }
        return returnUrl.replace(/([^:])\/\//g, '$1/');
    }
    _modifyOptions(options) {
        const resultOptions = {
            headers: options.headers || new HttpHeaders(),
            observe: 'response',
            params: options.params,
            reportProgress: options.reportProgress,
            responseType: 'text',
            withCredentials: options.withCredentials || this._withCredentials,
        };
        options.headers = options.headers || new HttpHeaders();
        resultOptions.headers = this._appendGlobalHeaders(resultOptions.headers);
        resultOptions.headers = this._tryAppendRegisteredCookiesToCustomHeaders(resultOptions.headers);
        return resultOptions;
    }
    _appendGlobalHeaders(headers) {
        let newHeaders = headers;
        for (const registeredHeader of this._globalHeaders) {
            if (registeredHeader.key && registeredHeader.value) {
                newHeaders = headers.set(registeredHeader.key, registeredHeader.value);
            }
        }
        return newHeaders;
    }
    _tryAppendRegisteredCookiesToCustomHeaders(headers) {
        let newHeaders = headers;
        for (const cookieToHeader of this._customCookieToHeaders) {
            const cookieValue = this._cookieStore.getCookie(cookieToHeader.cookieName);
            if (cookieValue) {
                newHeaders = headers.set(cookieToHeader.customHeaderName, cookieValue);
            }
        }
        return newHeaders;
    }
    async _invokeRequestInterceptorsAsync(url, method, data, headers) {
        for (const requestInterceptor of this._requestInterceptors) {
            const shouldIntercept = await requestInterceptor.beforeRequestAsync(url, method, data, headers);
            if (shouldIntercept) {
                return true;
            }
        }
        return false;
    }
    async _invokeResponseInterceptorsAsync(response, url, method, data, headers) {
        for (const responseInterceptor of this._responseInterceptors) {
            const shouldIntercept = await responseInterceptor.afterResponseAsync(response, url, method, data, headers);
            if (shouldIntercept) {
                return true;
            }
        }
        return false;
    }
    _convertAngularHeadersToHttpClientHeaders(headers) {
        return headers.keys().map(headerKey => {
            return new HttpHeader(headerKey, headers.get(headerKey) || '');
        });
    }
    _updateAngularHeadersFromHttpClientHeaders(httpClientHeaders, headers) {
        let newHeaders = headers;
        for (const clientHeader of httpClientHeaders) {
            const headerValue = headers.get(clientHeader.key);
            if (headerValue !== clientHeader.value) {
                newHeaders = headers.set(clientHeader.key, clientHeader.value);
            }
        }
        return newHeaders;
    }
}
CoolHttp.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.0", ngImport: i0, type: CoolHttp, deps: [{ token: i1.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
CoolHttp.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.0.0", ngImport: i0, type: CoolHttp });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.0", ngImport: i0, type: CoolHttp, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: i1.HttpClient }]; } });

class CoolHttpModule {
    static forRoot() {
        return {
            ngModule: CoolHttpModule,
            providers: [
                CoolHttp,
            ],
        };
    }
}
CoolHttpModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.0.0", ngImport: i0, type: CoolHttpModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
CoolHttpModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.0.0", ngImport: i0, type: CoolHttpModule, imports: [HttpClientModule] });
CoolHttpModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.0.0", ngImport: i0, type: CoolHttpModule, imports: [HttpClientModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.0.0", ngImport: i0, type: CoolHttpModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [HttpClientModule],
                }]
        }] });

/*
 * Public API Surface of http
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CookieStore, CoolHttp, CoolHttpModule, DEFAULT_REQUEST_OPTIONS, HttpError, HttpHeader };
